import React from 'react';
import { Neo, NeoGrid, Views } from '@singularsystems/neo-react';
import LetterAuthorisationVM from './LetterAuthorisationVM';
import { observer } from 'mobx-react';
import Signatories from './Components/Signatories';
import LetterAuthorisationBatchLookup from '../../Models/LetterAuthorisation/Lookups/LetterAuthorisationBatchLookup';
import BatchCriteria from './Components/BatchCriteria';
import NewAuthorisationBatch from './Components/NewAuthorisationBatch';
import AuthorisationBatch from './Components/AuthorisationBatch';
import * as Roles from '../../Models/Security/Roles';

class LetterAuthorisationViewParams {
    mode = {};
    id = {};
}

@observer
export default class LetterAuthorisationView extends Views.ViewBase<LetterAuthorisationVM, LetterAuthorisationViewParams> {
    static params = new LetterAuthorisationViewParams();

    constructor(props: unknown) {
        super("Letter Authorisation", LetterAuthorisationVM, props);

        this.onBatchCreated = this.onBatchCreated.bind(this);
    }

    protected viewParamsUpdated(): void | Promise<void> {
        return this.viewModel.handleModeChanged(this.viewParams.mode, this.viewParams.id);
    }

    public render() {
        const viewModel = this.viewModel;
        const hasStartDates = viewModel.mainPageManager.data.some(batch => batch.hasDifferentDates);
        const hasDescriptions = viewModel.mainPageManager.data.some(batch => batch.description);

        return (
            <div>
                {!viewModel.signatoriesVM && !viewModel.newBatchVM && !viewModel.authorisationBatchVM &&
                    <div>
                        <Neo.Card title="Letter Authorisation" icon="search">
                            <BatchCriteria criteria={viewModel.mainCriteria} autoRunner={viewModel.criteriaAutoRunner} />
                        </Neo.Card>

                        <Neo.Card>
                            <div className="mb-3 d-flex justify-content-between">
                                <div>
                                    {viewModel.authorisationService.hasRole(Roles.LetterAuthorisation.Create) &&
                                        <Neo.Button icon="plus" onClick={() => this.viewParams.mode.value = "new"}>Add Authorisation Batch</Neo.Button>}
                                </div>
                                <div>
                                    {viewModel.authorisationService.hasRole(Roles.LetterAuthorisation.ManageSignatories) &&
                                        <Neo.Button icon="cog" variant="secondary" onClick={() => this.viewParams.mode.value = "signatories"}>Manage Signatories</Neo.Button>}
                                </div>
                            </div>

                            <Neo.Pager pageManager={viewModel.mainPageManager}>
                                <NeoGrid.Grid<LetterAuthorisationBatchLookup>>
                                    {(item, meta) => (
                                        <NeoGrid.Row>
                                            <NeoGrid.Column display={meta.reportTypeName} />
                                            {hasStartDates && 
                                                <NeoGrid.Column display={meta.reportStartDate} />}
                                            <NeoGrid.Column display={meta.reportDate} />
                                            <NeoGrid.Column display={meta.entity} />
                                            {hasDescriptions &&
                                                <NeoGrid.Column display={meta.description} />}
                                            <NeoGrid.Column display={meta.letterCount} />
                                            <NeoGrid.Column display={meta.sentCount} />
                                            <NeoGrid.Column display={meta.savedCount} />
                                            <NeoGrid.ButtonColumn label="">
                                                <div style={{ fontSize: "15px"}}>
                                                    {item.isAuthorised && 
                                                        <Neo.Badge isLarge variant="success">Authorised</Neo.Badge>}
                                                    {!item.isAuthorised && item.remaining > 0 &&
                                                        <Neo.Badge isLarge variant="warning">Auth. Remaining: {item.remaining}</Neo.Badge>}
                                                    {(item.isAuthorised || item.remaining === 0) && item.remainingToSend > 0 &&
                                                        <Neo.Badge isLarge className="ms-1" variant="light">Unsent: {item.remainingToSend}</Neo.Badge>}
                                                </div>
                                            </NeoGrid.ButtonColumn>
                                            <NeoGrid.ButtonColumn>
                                                <Neo.Button icon="arrow-right" variant="info" size="sm" onClick={() => this.loadBatch(item)}>View</Neo.Button>
                                            </NeoGrid.ButtonColumn>
                                        </NeoGrid.Row>
                                    )}
                                </NeoGrid.Grid>
                            </Neo.Pager>
                        </Neo.Card>
                    </div>}

                {viewModel.signatoriesVM && <Signatories viewModel={viewModel.signatoriesVM} onBackClicked={() => this.viewParams.mode.reset()} />}
                {viewModel.newBatchVM && <NewAuthorisationBatch viewModel={viewModel.newBatchVM} onBackClicked={() => this.viewParams.mode.reset()} onBatchCreated={this.onBatchCreated} />}
                {viewModel.authorisationBatchVM && <AuthorisationBatch viewModel={viewModel.authorisationBatchVM} onBackClicked={() => this.viewParams.mode.reset()} />}
            </div>
        );
    }

    private loadBatch(batch: LetterAuthorisationBatchLookup) {
        this.loadBatchId(batch.letterAuthorisationBatchId, false);
    }

    private onBatchCreated(batchId: number) {
        this.loadBatchId(batchId, true);
    }

    private loadBatchId(batchId: number, isNew: boolean) {
        this.viewModel.isLoadingNewBatch = isNew;
        this.viewParams.setValues({ mode: "view", id: batchId });
    }
}